import { DateTime } from "luxon";
import { Shift } from "../../schema";
import { SHIFT_CODES_KEYS } from "../../shiftCodes";
import { HOURS_PER_SHIFT, UK_OT13S_PAY, UK_TAX_PAYER_WEEKDAY_PAY, UK_NON_TAX_PAYER_WEEKDAY_PAY, ShiftData, makeShiftData, getMonthDateBoundaries } from "../common";

export const getWeekdaysInMonth = (month: number, year: number) => {
  const weekdays = Array.from(
    { length: DateTime.utc(year, month).endOf('month').day },
    (_, i) => DateTime.utc(year, month, i + 1)
  ).filter(date => date.weekday !== 6 && date.weekday !== 7).length;
  console.log(`[UK Weekday Pay] Number of weekdays in ${month}/${year}: ${weekdays}`);
  return weekdays;
}

export const getUKWeekdayMonthHourlyPay = (isUKTaxPayer: boolean, month: number, year: number) => {
  const dailyPayAmount = isUKTaxPayer ? UK_TAX_PAYER_WEEKDAY_PAY : UK_NON_TAX_PAYER_WEEKDAY_PAY;

  console.log(`[UK Weekday Pay] Calculated daily pay amount: £${dailyPayAmount.toFixed(2)} for ${isUKTaxPayer ? 'UK Tax Payer' : 'UK Non-Tax Payer'}`);

  const weekdays = getWeekdaysInMonth(month, year);
  const dailyPay = dailyPayAmount / weekdays;
  const hourlyPay = dailyPay / HOURS_PER_SHIFT;
  console.log(`[UK Weekday Pay] Calculated hourly pay rate: £${hourlyPay.toFixed(2)}`);
  return hourlyPay;
}

export const calculateStandardWeekdayPayForShifts = async (isUKTaxPayer: boolean, allShifts: Shift[], standardShiftCodeIds: string[], month: number, year: number): Promise<ShiftData[]> => {
  const weekdayShifts = allShifts.filter((shift) => {
    return standardShiftCodeIds.includes(shift.shift_code);
  });

  console.log(`[UK Weekday Pay] Found ${weekdayShifts.length} standard weekday shifts`);

  const hourlyPay = getUKWeekdayMonthHourlyPay(isUKTaxPayer, month, year);

  return weekdayShifts.map(shift => {
    const hours = shift.end_date_time.diff(shift.start_date_time, "hours").hours;
    const shift_pay = hours * hourlyPay;
    return makeShiftData(shift, shift_pay);
  });
}

export const calculateWeekdayOvertimePayForShifts = async (allShifts: Shift[], overtimeShiftCodeIds: string[]): Promise<ShiftData[]> => {
  const overtimeShifts = allShifts.filter((shift) => {
    return overtimeShiftCodeIds.includes(shift.shift_code);
  });

  console.log(`[UK Weekday Pay] Found ${overtimeShifts.length} overtime weekday shifts`);

  return overtimeShifts.map(shift => {
    const hours = shift.end_date_time.diff(shift.start_date_time, "hours").hours;
    const shift_pay = hours * UK_OT13S_PAY;
    return makeShiftData(shift, shift_pay);
  });
}

export const calculateTotalWeekdayPayForShifts = async (isUKTaxPayer: boolean, allShifts: Shift[], standardShiftCodeIds: string[], overtimeShiftCodeIds: string[], month: number, year: number): Promise<ShiftData[]> => {
  const weekdayPay = await calculateStandardWeekdayPayForShifts(isUKTaxPayer, allShifts, standardShiftCodeIds, month, year);
  const weekdayOvertimePay = await calculateWeekdayOvertimePayForShifts(allShifts, overtimeShiftCodeIds);

  return [
    ...weekdayPay,
    ...weekdayOvertimePay
  ];
}

export const weekdayPayCalculator = async (isUKTaxPayer: boolean, shifts: Shift[], month: number, year: number): Promise<ShiftData[]> => {
  console.log(`[UK Weekday Pay] Starting weekday pay calculation for ${month}/${year}`);

  const standardShiftCodes = SHIFT_CODES_KEYS.filter(sc => ['6-2 S', '2-10 S', '10-6 S'].includes(sc));
  const overtimeShiftCodes = SHIFT_CODES_KEYS.filter(sc => sc === 'OT13 S');

  const { start, end } = getMonthDateBoundaries(DateTime.fromObject({ year: year, month: month }, { zone: 'UTC' }));

  console.log(start, end);
  console.log(shifts);

  const filteredShifts = shifts.filter(shift =>
    shift.start_date_time >= start &&
    shift.start_date_time < end
  );
  console.log(`[UK Weekday Pay] Found ${filteredShifts.length} total shifts for month`);

  return await calculateTotalWeekdayPayForShifts(isUKTaxPayer, filteredShifts, standardShiftCodes, overtimeShiftCodes, month, year);
}
